import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp } from 'lucide-react';

const easeSmooth: [number, number, number, number] = [0.25, 0.46, 0.45, 0.94];
const easeParticle: [number, number, number, number] = [0.4, 0, 0.2, 1];

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.2 },
  },
};

const leftBlock = {
  hidden: { opacity: 0, x: -30 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: easeSmooth } },
};

const centerBlock = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.7, ease: easeSmooth, delay: 0.1 } },
};

const rightBlock = {
  hidden: { opacity: 0, x: 30 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: easeSmooth } },
};

const GlowSystemSchema: React.FC = () => {
  const steps = [
    { label: 'Capture', desc: 'Landing Page optimisée' },
    { label: 'Éducation', desc: 'Séquences email stratégiques' },
    { label: 'Conversion', desc: 'Checkout & Vente' },
  ];

  const Particle = ({ direction, delay = 0 }: { direction: 'x' | 'y'; delay?: number }) => (
    <motion.div
      animate={direction === 'x' ? { x: ['-100%', '250%'] } : { y: ['-100%', '250%'] }}
      transition={{ duration: 3, repeat: Infinity, ease: easeParticle, delay }}
      className={direction === 'x' ? 'w-10 h-full rounded-full' : 'w-full h-5 rounded-full'}
      style={{
        background:
          direction === 'x'
            ? 'linear-gradient(90deg, transparent, hsl(var(--rose)), transparent)'
            : 'linear-gradient(180deg, transparent, hsl(var(--rose)), transparent)',
      }}
    />
  );

  return (
    <motion.div
      className="w-full max-w-4xl mx-auto py-12"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
    >
      <div className="relative flex flex-col md:flex-row items-center justify-between gap-8">

        {/* ── BLOC 1 : TRAFIC ──────────────────────────── */}
        <motion.div
          variants={leftBlock}
          className="z-10 w-full md:w-64 p-6 bg-background border border-border rounded-2xl shadow-soft text-center"
        >
          <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2 font-semibold font-body">
            Étape 01
          </p>
          <h4 className="text-foreground font-display font-semibold mb-1">Ton Contenu</h4>
          <p className="text-xs text-muted-foreground leading-relaxed italic font-body">
            Instagram, Pinterest, Blog…
            <br />
            Le carburant brut.
          </p>
        </motion.div>

        {/* ── LIGNE ANIMÉE 1 — Desktop ───── */}
        <div className="hidden md:block absolute left-[22%] w-[15%] h-[2px] bg-border overflow-hidden">
          <Particle direction="x" />
          <Particle direction="x" delay={1.2} />
        </div>

        {/* ── LIGNE ANIMÉE 1 — Mobile ──────── */}
        <div className="md:hidden w-[2px] h-10 bg-border overflow-hidden mx-auto">
          <Particle direction="y" />
          <Particle direction="y" delay={1.2} />
        </div>

        {/* ── BLOC 2 : L'ÉCOSYSTÈME GLOW (CŒUR) ───────── */}
        <motion.div
          variants={centerBlock}
          className="z-10 w-full md:w-80 p-8 rounded-2xl shadow-dark-card relative overflow-hidden group"
          style={{
            background: 'hsl(var(--dark))',
            borderTop: '2px solid hsl(var(--rose) / 0.5)',
          }}
        >
          {/* Pulsing radial glow */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            animate={{ opacity: [0.05, 0.12, 0.05] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              background: 'radial-gradient(circle at 50% 50%, hsl(var(--rose) / 0.15), transparent)',
            }}
          />

          <div className="relative text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: easeSmooth, delay: 0.3 }}
              className="inline-block px-3 py-1 rounded-full mb-4"
              style={{
                background: 'hsl(var(--off-white) / 0.1)',
                border: '1px solid hsl(var(--off-white) / 0.2)',
              }}
            >
              <span className="text-[11px] font-bold tracking-widest uppercase font-body" style={{ color: 'hsl(var(--off-white))' }}>
                L'Écosystème Glow
              </span>
            </motion.div>

            <ul className="space-y-4 text-left">
              {steps.map((item, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 + i * 0.2, duration: 0.4, ease: easeSmooth }}
                  className="flex items-start gap-3"
                >
                  <div
                    className="mt-1 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ border: '1px solid hsl(var(--rose) / 0.4)' }}
                  >
                    <motion.div
                      className="w-1 h-1 rounded-full"
                      style={{ background: 'hsl(var(--rose))' }}
                      animate={{ scale: [1, 1.4, 1] }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut', delay: i * 0.4 }}
                    />
                  </div>
                  <div>
                    <p className="text-sm font-medium leading-none font-body" style={{ color: 'hsl(var(--off-white))' }}>
                      {item.label}
                    </p>
                    <p className="text-[11px] mt-1 text-muted-foreground font-body">{item.desc}</p>
                  </div>
                </motion.li>
              ))}
            </ul>
          </div>
        </motion.div>

        {/* ── LIGNE ANIMÉE 2 — Desktop ─────────────────── */}
        <div className="hidden md:block absolute right-[22%] w-[15%] h-[2px] bg-border overflow-hidden">
          <Particle direction="x" delay={0.8} />
          <Particle direction="x" delay={2} />
        </div>

        {/* ── LIGNE ANIMÉE 2 — Mobile ──────────────────── */}
        <div className="md:hidden w-[2px] h-10 bg-border overflow-hidden mx-auto">
          <Particle direction="y" delay={0.8} />
          <Particle direction="y" delay={2} />
        </div>

        {/* ── BLOC 3 : RÉSULTATS ───────────────────────── */}
        <motion.div
          variants={rightBlock}
          className="z-10 w-full md:w-64 p-6 bg-background border border-border rounded-2xl shadow-soft text-center"
        >
          <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2 font-semibold font-body">
            Résultat
          </p>
          <h4 className="text-foreground font-display font-semibold mb-1">Liberté & ROI</h4>
          <p className="text-xs text-muted-foreground leading-relaxed font-body">
            Ventes automatiques &
            <br />
            Données exploitables.
          </p>
          <div className="mt-3">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: easeSmooth, delay: 0.6 }}
            >
              <TrendingUp className="mx-auto" size={22} strokeWidth={1.5} style={{ color: 'hsl(var(--rose))' }} />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default GlowSystemSchema;
